import { useState, useEffect, useRef, useMemo } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  useTimeTrackerStore,
  TimeEntry,
  Client,
  initTimeTrackerState,
} from "../store/timeTrackerStore";
import {
  validateSingleRunningEntry,
  formatDuration,
  filterEntriesByDatePreset,
  groupEntriesByDay,
  sortClientsForPicker,
  DATE_PRESETS,
  DATE_PRESET_LABELS,
} from "../utils/TimeTrackerUtils";
import { smoothEntries, SmoothedEntry } from "../utils/smoothing";
import SettingsModal from "./SettingsModal";
import {
  enumCodec,
  setUrlParam,
  setUrlParams,
  stringCodec,
  stringSetCodec,
  useUrlParam,
} from "../utils/useUrlState";
import { modalCodec } from "../utils/modalParam";
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Input,
  Kbd,
  MultiSelectChips,
  SegmentedControl,
  Select,
  Tabs,
  useToast,
} from "@lazar/ui";

const datePresetCodec = enumCodec(DATE_PRESETS, "week");
const clientsCodec = stringSetCodec();
const searchCodec = stringCodec("");

// A SmoothedEntry carries sourceIds; a raw TimeEntry does not. Used to surface
// the merged-count badge and to gate read-only rendering in the smoothed view.
const isSmoothed = (e: TimeEntry): e is SmoothedEntry => "sourceIds" in e;

// The entries-list source: raw (newest first) as today, or the smoothed
// completed entries with the running entry passed through untouched. Smoothing
// runs on the full set before any date/client/search filter so runs form
// across the whole timeline, then the existing pipeline filters and groups.
function buildEntryList(
  sortedDesc: TimeEntry[],
  showSmoothed: boolean,
  thresholdMs: number,
): TimeEntry[] {
  if (!showSmoothed) return sortedDesc;
  const completed = sortedDesc.filter((e) => e.endTime !== undefined);
  const running = sortedDesc.find((e) => e.endTime === undefined);
  const merged: TimeEntry[] = smoothEntries(completed, thresholdMs);
  if (running) merged.push(running);
  return merged.sort((a, b) => b.startTime - a.startTime);
}

const TimeTracker = () => {
  // Analytics and Settings share one URL param so at most one can be open; see
  // utils/modalParam.ts. The stop-timer picker (local `isModalOpen` below) is
  // coordinated against it so it can't stack with either.
  const [modal, setModal] = useUrlParam("modal", modalCodec);
  const { addToast } = useToast();

  useEffect(() => {
    initTimeTrackerState();
  }, []);

  const {
    getTimeEntriesSorted,
    addTimeEntry,
    updateTimeEntry,
    getTimeEntry,
    deleteTimeEntry,
    getClients,
    getAllClients,
    getClient,
    getTimeEntries,
    settings,
  } = useTimeTrackerStore();

  const [currentEntry, setCurrentEntry] = useState<string | null>(null);
  const [timeDisplay, setTimeDisplay] = useState("");
  const [timerInterval, setTimerInterval] = useState<number | null>(null);
  const [editingEntry, setEditingEntry] = useState<string | null>(null);
  const [editDescription, setEditDescription] = useState("");
  const [editStartTime, setEditStartTime] = useState<Date | null>(null);
  const [editEndTime, setEditEndTime] = useState<Date | null>(null);
  const [clientId, setClientId] = useState<string>("c1");
  const [editClientId, setEditClientId] = useState<string>("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDescription, setModalDescription] = useState("");
  const [modalClientId, setModalClientId] = useState<string>("c1");
  const [entryToSave, setEntryToSave] = useState<string | null>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "KeyS") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }
      e.preventDefault();
      setModal(modal === "settings" ? "none" : "settings");
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modal, setModal]);

  const [showStaleBanner, setShowStaleBanner] = useState(false);
  const [staleEntryId, setStaleEntryId] = useState<string | null>(null);
  // Stable mount-time timestamp, only used as a defensive fallback for the stale
  // banner's "running since" label when the entry can't be found. staleEntryId
  // always points at a real running entry (set alongside setShowStaleBanner), so
  // this fallback effectively never renders; capturing it once at mount via a lazy
  // useState keeps Date.now() out of render (react-hooks/purity).
  const [bannerFallbackTime] = useState(() => Date.now());

  // Capture the document title once at mount (lazy initializer, so it's read out
  // of render and never re-set), then restore it on unmount and whenever no timer
  // is running. Previously this was seeded by a setState inside the title effect,
  // which react-hooks/set-state-in-effect flags as a cascading-render risk.
  const [originalTitle] = useState(() => document.title || "Lemon Pi");

  const [datePreset, setDatePreset] = useUrlParam("date", datePresetCodec);
  const [selectedClientIds, setSelectedClientIds] = useUrlParam(
    "clients",
    clientsCodec,
  );
  const [searchQuery, setSearchQuery] = useUrlParam("q", searchCodec, {
    history: "replace",
  });
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Local view toggle for the entries list. Only meaningful when smoothing is
  // enabled in settings; defaults to the smoothed view so the feature is the
  // first thing the user sees after turning it on. Raw is the escape hatch for
  // editing, since smoothed rows are derived and have no single entry to edit.
  const [smoothView, setSmoothView] = useState(true);
  const showSmoothed = settings.smoothing.enabled && smoothView;

  const toggleClientFilter = (id: string) => {
    const next = new Set(selectedClientIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedClientIds(next);
  };

  const clearFilters = () => {
    // Batched into a single history entry so back-button restores all three
    // filters at once, not one at a time.
    setUrlParams({
      date: datePresetCodec.serialize("all"),
      clients: clientsCodec.serialize(new Set()),
      q: searchCodec.serialize(""),
    });
  };

  // Reflect the running timer in the tab title; restore the captured title when
  // no timer is running and on unmount. originalTitle is captured at mount above,
  // so this effect only performs the document.title side-effect (no setState).
  useEffect(() => {
    if (currentEntry && timeDisplay) {
      document.title = `🍋 ${timeDisplay} - Lemon Pi`;
    } else {
      document.title = originalTitle;
    }

    return () => {
      document.title = originalTitle;
    };
  }, [currentEntry, timeDisplay, originalTitle]);

  const sortedEntries = getTimeEntriesSorted("startTime", true);
  const baseEntries = buildEntryList(
    sortedEntries,
    showSmoothed,
    settings.smoothing.thresholdMinutes * 60_000,
  );
  const dateFilteredEntries = filterEntriesByDatePreset(
    baseEntries,
    datePreset,
  );
  const clientFilteredEntries =
    selectedClientIds.size === 0
      ? dateFilteredEntries
      : dateFilteredEntries.filter(
          (e) =>
            e.endTime === undefined ||
            (e.clientId !== undefined && selectedClientIds.has(e.clientId)),
        );
  const searchTerm = searchQuery.trim().toLowerCase();
  const filteredEntries =
    searchTerm === ""
      ? clientFilteredEntries
      : clientFilteredEntries.filter(
          (e) =>
            e.endTime === undefined ||
            e.description.toLowerCase().includes(searchTerm),
        );
  const dayGroups = groupEntriesByDay(filteredEntries);

  useEffect(() => {
    validateSingleRunningEntry(sortedEntries);
  }, [sortedEntries]);

  // If the running entry vanishes from the store (e.g. prune-all triggered
  // from Settings, future sync), tear down the local timer state so the UI
  // doesn't keep ticking against a deleted entry. This is a genuine
  // external-store-sync effect: the trigger is the store changing under us, not
  // a render or an event we own, so there's no handler to move the resets into.
  // react-hooks/set-state-in-effect is disabled here deliberately — the setState
  // calls are the teardown, and they keep the running-timer invariant (no entry
  // without endTime is left ticking locally).
  useEffect(() => {
    if (currentEntry && !getTimeEntry(currentEntry)) {
      /* eslint-disable react-hooks/set-state-in-effect */
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      setCurrentEntry(null);
      setTimeDisplay("");
      /* eslint-enable react-hooks/set-state-in-effect */
    }
  }, [sortedEntries, currentEntry, timerInterval, getTimeEntry]);

  const clients = getClients().sort((a: Client, b: Client) =>
    a.name.localeCompare(b.name),
  );

  // The stop-timer picker (and its 1–9 shortcuts + default selection) follows
  // the user's manual pickerOrder; everything else here — filter chips, edit
  // dropdown — stays alphabetical. Same client set as `clients`, reordered.
  const pickerClients = useMemo(() => sortClientsForPicker(clients), [clients]);

  // The edit dropdown must include the entry's currently-assigned client even
  // if it has been archived — otherwise opening edit on a historical entry
  // would silently rewrite clientId to whatever option happens to be first.
  const editClientOptions = useMemo(() => {
    if (!editClientId || clients.some((c) => c.id === editClientId)) {
      return clients;
    }
    const archived = getClient(editClientId);
    if (!archived) return clients;
    return [
      { ...archived, name: `${archived.name} (archived)` },
      ...clients,
    ];
  }, [clients, editClientId, getClient]);

  // Filter chips. Active clients always show; any archived clients that are
  // *currently* in the selected filter (typically from an analytics deep-link)
  // get appended so the user can deselect them. Otherwise an archived id in
  // the URL would be invisible — only "All clients" could clear it.
  const chipClients = useMemo<Client[]>(() => {
    if (selectedClientIds.size === 0) return clients;
    const activeIds = new Set(clients.map((c) => c.id));
    const extras: Client[] = [];
    selectedClientIds.forEach((id) => {
      if (activeIds.has(id)) return;
      const c = getClient(id);
      if (c && c.archivedAt !== undefined) extras.push(c);
    });
    if (extras.length === 0) return clients;
    extras.sort((a, b) => a.name.localeCompare(b.name));
    return [...clients, ...extras];
  }, [clients, selectedClientIds, getClient]);

  // Keep the picker's selected client valid when the client list changes in the
  // store (a client is archived/deleted/reordered out from under the current
  // selection). External-store-sync, so the default-reset belongs in an effect,
  // not an event handler. Disabling set-state-in-effect here preserves the
  // load-bearing "default to the top-priority picker row" behavior.
  useEffect(() => {
    if (pickerClients.length > 0) {
      if (!pickerClients.some((cl) => cl.id === modalClientId)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setModalClientId(pickerClients[0].id);
      }
    }
  }, [pickerClients, modalClientId]);

  // Same external-store-sync, for the start-timer client selection.
  useEffect(() => {
    if (clients.length > 0 && !clients.some((cl) => cl.id === clientId)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setClientId(clients[0].id);
    }
  }, [clients, clientId]);

  useEffect(() => {
    if (selectedClientIds.size === 0) return;
    // Use ALL clients (active + archived) — archived ids are legitimate filter
    // targets, especially via analytics deep-links into historical data. Only
    // truly-deleted ids get pruned.
    const valid = new Set(getAllClients().map((c) => c.id));
    let changed = false;
    const next = new Set<string>();
    selectedClientIds.forEach((id) => {
      if (valid.has(id)) next.add(id);
      else changed = true;
    });
    // Drop stale ids via replaceState — auto-cleanup of an invalid URL
    // shouldn't add a history entry the user has to back-button past.
    if (changed) setUrlParam("clients", clientsCodec.serialize(next), "replace");
  }, [getAllClients, selectedClientIds]);

  // Re-default the picker selection when the stop-picker opens (or the list
  // changes while it's open). External-store / open-state sync.
  useEffect(() => {
    if (isModalOpen && pickerClients.length > 0) {
      if (!pickerClients.some((cl) => cl.id === modalClientId)) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setModalClientId(pickerClients[0].id);
      }
    }
  }, [isModalOpen, pickerClients, modalClientId]);

  const noActiveClients = clients.length === 0;

  // Rehydrate the local timer from a running entry already in the store — the
  // reload / persistence path. On mount (or when a running entry first appears)
  // adopt it, restart the ticking interval from its persisted startTime, and
  // raise the stale banner if it's over an hour old. This is the canonical
  // external-store-sync effect; the setState calls reconstruct local UI state
  // from persisted data, so set-state-in-effect is disabled deliberately. The
  // `!currentEntry` guard keeps it from adopting a second running entry, holding
  // the running-timer invariant. Deliberately keyed only on sortedEntries —
  // adding currentEntry/startTimerInterval would re-run the adopt on every local
  // timer change, which is exactly what the guard is there to avoid.
  useEffect(() => {
    const runningEntry = sortedEntries.find((entry) => !entry.endTime);
    if (runningEntry && !currentEntry) {
      /* eslint-disable react-hooks/set-state-in-effect */
      setCurrentEntry(runningEntry.id);
      setClientId(runningEntry.clientId || "c1");
      startTimerInterval(runningEntry.startTime);

      const ONE_HOUR = 60 * 60 * 1000;
      if (Date.now() - runningEntry.startTime > ONE_HOUR) {
        setShowStaleBanner(true);
        setStaleEntryId(runningEntry.id);
      }
      /* eslint-enable react-hooks/set-state-in-effect */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedEntries]);

  // Keep the start-timer client selection in step with the adopted running
  // entry's client. External-store-sync.
  useEffect(() => {
    if (currentEntry) {
      const entry = getTimeEntry(currentEntry);
      if (entry) {
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setClientId(entry.clientId || "c1");
      }
    }
  }, [currentEntry, getTimeEntry]);

  useEffect(() => {
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [timerInterval]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;

      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement ||
        e.target instanceof HTMLSelectElement ||
        isModalOpen ||
        editingEntry
      ) {
        return;
      }

      e.preventDefault();

      if (currentEntry) {
        stopTimer();
      } else {
        startTimer();
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentEntry, isModalOpen, editingEntry]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isModalOpen) return;
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        searchInputRef.current?.focus();
        searchInputRef.current?.select();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  useEffect(() => {
    const handleModalKeyDown = (e: KeyboardEvent) => {
      if (!isModalOpen) return;
      if (e.key === "Escape") {
        resumeFromModal();
      } else if (e.key === "Enter") {
        e.preventDefault();
        saveEntryFromModal();
      } else if (e.key === "Backspace" && e.metaKey) {
        e.preventDefault();
        discardFromModal();
      } else if (
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey &&
        /^[1-9]$/.test(e.key)
      ) {
        const index = parseInt(e.key, 10) - 1;
        if (index < Math.min(pickerClients.length, 9)) {
          e.preventDefault();
          saveEntryFromModal(pickerClients[index].id);
        }
      }
    };

    window.addEventListener("keydown", handleModalKeyDown);
    return () => {
      window.removeEventListener("keydown", handleModalKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen, modalDescription, modalClientId, entryToSave, pickerClients]);

  // Declared as a hoisted `function` (not a `const` arrow) so the effects above
  // can reference it without a temporal-dead-zone access (react-hooks/immutability
  // flags reading a `const` before its declaration). The body still closes over
  // the latest render's state — it's only ever called inside effects/handlers that
  // run after the component body has fully evaluated.
  function startTimerInterval(startTime: number) {
    if (timerInterval) {
      clearInterval(timerInterval);
    }

    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      setTimeDisplay(formatDuration(elapsed));
    }, 1000);

    setTimeDisplay(formatDuration(Date.now() - startTime));

    setTimerInterval(interval);
  }

  function startTimer() {
    const entries = getTimeEntries();
    const runningEntries = entries.filter((entry) => !entry.endTime);
    if (runningEntries.length > 0) {
      throw new Error(
        "Cannot start a new timer while another timer is already running.",
      );
    }

    const id = `te_${Date.now()}`;
    const startTime = Date.now();

    addTimeEntry({
      id,
      description: "",
      startTime,
      clientId,
    });

    setCurrentEntry(id);
    startTimerInterval(startTime);
  }

  function stopTimer() {
    if (!currentEntry) return;

    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    const entry = getTimeEntry(currentEntry);
    if (entry) {
      setModalDescription(entry.description || "");
      // Default the picker to the top-priority client (row 1 of the user's
      // custom order). We deliberately do NOT seed from entry.clientId: there's
      // no client selector at start, so a running entry's clientId is only the
      // alphabetical start placeholder, not a deliberate choice. Seeding it made
      // the highlight land on whatever row that placeholder occupies after a
      // reorder — typically not row 1. (Regression from e210880.)
      setModalClientId(
        pickerClients.length > 0
          ? pickerClients[0].id
          : entry.clientId ?? "c1",
      );
      setEntryToSave(currentEntry);

      // Close whichever URL modal (Analytics/Settings) is open so the picker
      // doesn't stack on top of it.
      setModal("none");
      setIsModalOpen(true);
    }
  }

  function saveEntryFromModal(overrideClientId?: string) {
    if (!entryToSave) return;
    if (modalDescription.trim() === "") {
      addToast("Please enter a description", "warning");
      return;
    }

    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    updateTimeEntry(entryToSave, {
      description: modalDescription,
      clientId: overrideClientId ?? modalClientId,
      endTime: Date.now(),
    });

    setCurrentEntry(null);
    setTimeDisplay("");
    setEntryToSave(null);
    setIsModalOpen(false);
  }

  // Opening any URL modal (Analytics or Settings) while the stop picker is up
  // resumes the timer and closes the picker, rather than stacking on top of it.
  useEffect(() => {
    if (modal !== "none" && isModalOpen) {
      resumeFromModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modal]);

  function resumeFromModal() {
    if (entryToSave) {
      setCurrentEntry(entryToSave);
      const entry = getTimeEntry(entryToSave);
      if (entry) {
        startTimerInterval(entry.startTime);
      }
    }
    setEntryToSave(null);
    setIsModalOpen(false);
  }

  function discardFromModal() {
    if (entryToSave) {
      deleteTimeEntry(entryToSave);
      setCurrentEntry(null);
    }
    setEntryToSave(null);
    setIsModalOpen(false);
  }

  const resumeTimer = (entryId: string) => {
    if (currentEntry) {
      stopTimer();
    }

    const entry = getTimeEntry(entryId);
    if (entry) {
      setCurrentEntry(entryId);
      startTimerInterval(entry.startTime);
    }
  };

  const startEditingEntry = (entry: TimeEntry) => {
    setEditingEntry(entry.id);
    setEditDescription(entry.description);
    setEditClientId(entry.clientId || "c1");

    setEditStartTime(new Date(entry.startTime));
    if (entry.endTime) {
      setEditEndTime(new Date(entry.endTime));
    } else {
      setEditEndTime(null);
    }
  };

  const cancelEdit = () => {
    setEditingEntry(null);
  };

  const saveEdit = () => {
    if (!editingEntry || !editStartTime) return;

    const startTimeMs = editStartTime.getTime();
    const updates: Partial<TimeEntry> = {
      description: editDescription,
      startTime: startTimeMs,
      clientId: editClientId,
    };

    if (editEndTime) {
      const endTimeMs = editEndTime.getTime();

      if (startTimeMs >= endTimeMs) {
        addToast("End time must be after start time", "error");
        return;
      }

      updates.endTime = endTimeMs;
    } else {
      updates.endTime = undefined;
    }

    updateTimeEntry(editingEntry, updates);
    setEditingEntry(null);
  };

  const handleDeleteEntry = (id: string) => {
    const entry = getTimeEntry(id);
    if (!entry) return;

    if (id === currentEntry) {
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
      setCurrentEntry(null);
    }

    const savedEntry = { ...entry };
    deleteTimeEntry(id);
    addToast("Entry deleted", "success", {
      action: {
        label: "Undo",
        onClick: () => addTimeEntry(savedEntry),
      },
    });
  };

  const getClientLabel = (clId?: string) => {
    if (!clId) return null;
    const client = getClient(clId);
    if (!client) return null;

    if (clId === "running") {
      return (
        <Badge className="animate-pulse bg-coral-500 text-ink">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-coral-600 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-ink"></span>
          </span>
          <span className="font-black">RECORDING</span>
        </Badge>
      );
    }

    const isArchived = client.archivedAt !== undefined;
    return (
      <Badge
        style={{
          backgroundColor: client.color,
          color: "var(--color-ink)",
        }}
      >
        {isArchived ? `${client.name} (archived)` : client.name}
      </Badge>
    );
  };

  const dismissStaleBanner = () => {
    setShowStaleBanner(false);
    setStaleEntryId(null);
  };

  const stopStaleTimer = () => {
    if (staleEntryId) {
      stopTimer();
    }
    dismissStaleBanner();
  };

  return (
    <div className="space-y-6">
      {/* Stale timer banner */}
      {showStaleBanner && staleEntryId && (
        <Alert variant="warning">
          <div className="flex items-center justify-between gap-4">
            <span className="font-bold">
              You have a timer running since{" "}
              {new Date(
                getTimeEntry(staleEntryId)?.startTime ?? bannerFallbackTime,
              ).toLocaleString()}
            </span>
            <div className="flex gap-2">
              <Button onClick={stopStaleTimer} variant="danger">
                Stop
              </Button>
              <Button onClick={dismissStaleBanner}>Continue</Button>
            </div>
          </div>
        </Alert>
      )}

      {/* Time entries list */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <div className="space-y-2">
            <div className="text-2xl flex items-center">
              <svg
                className="w-7 h-7 mr-2 text-ink dark:text-cream"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <circle cx="12" cy="12" r="10" strokeWidth="2.5" />
                <path d="M12 6v6l4 2" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              <span className="font-black">Tracked time</span>
            </div>
            {currentEntry && (
              <div className="text-2xl font-black font-mono text-coral-600 dark:text-coral-500 truncate max-w-xs">
                {timeDisplay}
              </div>
            )}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {/* Settings */}
            <Button
              onClick={() => setModal("settings")}
              icon
              title="Settings (S)"
              aria-label="Settings"
            >
              <svg
                className="w-4 h-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
              >
                <path
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Button>

            {!currentEntry ? (
              <Button
                onClick={startTimer}
                disabled={!!currentEntry || noActiveClients}
                title={
                  noActiveClients
                    ? "Add or restore a client first"
                    : undefined
                }
                variant="primary"
              >
                Start Timer
                <Kbd>Space</Kbd>
              </Button>
            ) : (
              <Button
                onClick={stopTimer}
                variant="danger"
              >
                Stop Timer
                <Kbd>Space</Kbd>
              </Button>
            )}
          </div>
        </div>

        {sortedEntries.length === 0 ? (
          <EmptyState>
            <p className="text-ink dark:text-cream font-bold">
              No time entries yet. Hit{" "}
              <Kbd>Space</Kbd> to start your first slice.
            </p>
          </EmptyState>
        ) : (
          <>
            <div className="relative mb-3">
              <Input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    setSearchQuery("");
                    searchInputRef.current?.blur();
                  }
                }}
                placeholder="Search descriptions"
                aria-label="Search entries by description"
                className="pr-20"
              />
              <Kbd
                className="absolute right-2 top-1/2 -translate-y-1/2 opacity-60 pointer-events-none"
                aria-hidden="true"
              >
                ⌘K
              </Kbd>
            </div>

            {settings.smoothing.enabled && (
              <div className="flex items-center gap-2 flex-wrap mb-3">
                <SegmentedControl
                  ariaLabel="Entry view mode"
                  options={[
                    { value: "smoothed", label: "Smoothed" },
                    { value: "raw", label: "Raw" },
                  ]}
                  value={showSmoothed ? "smoothed" : "raw"}
                  onChange={(value) => setSmoothView(value === "smoothed")}
                />
                {showSmoothed && (
                  <span className="text-xs text-ink-soft dark:text-cream/60">
                    Read-only — switch to Raw to edit.
                  </span>
                )}
              </div>
            )}

            <Tabs
              className="mb-3"
              ariaLabel="Filter entries by date"
              controls="tracked-entries"
              value={datePreset}
              onChange={setDatePreset}
              options={DATE_PRESETS.map((preset) => ({
                value: preset,
                label: DATE_PRESET_LABELS[preset],
              }))}
            />

            {clients.length > 0 && (
              <MultiSelectChips
                className="mb-4"
                ariaLabel="Filter entries by client"
                selected={selectedClientIds}
                onToggle={toggleClientFilter}
                reset={
                  <button
                    type="button"
                    onClick={() => setSelectedClientIds(new Set())}
                    aria-pressed={selectedClientIds.size === 0}
                    className={
                      selectedClientIds.size === 0
                        ? "brut-chip brut-chip-active"
                        : "brut-chip"
                    }
                  >
                    All clients
                  </button>
                }
                options={chipClients.map((client) => {
                  const isArchived = client.archivedAt !== undefined;
                  return {
                    value: client.id,
                    className: isArchived ? "opacity-70" : undefined,
                    leading: (
                      <span
                        className="brut-chip-dot"
                        style={{ backgroundColor: client.color }}
                        aria-hidden="true"
                      />
                    ),
                    label: (
                      <>
                        <span className={isArchived ? "line-through" : ""}>
                          {client.name}
                        </span>
                        {isArchived && (
                          <span className="ml-1 text-xs opacity-70">(archived)</span>
                        )}
                      </>
                    ),
                  };
                })}
              />
            )}

            <div id="tracked-entries" role="region" aria-label="Tracked entries">
            {filteredEntries.length === 0 ? (
              <EmptyState>
                <p className="text-ink dark:text-cream font-bold">
                  No entries match these filters.{" "}
                  <button
                    onClick={clearFilters}
                    className="underline font-black"
                  >
                    Show all
                  </button>
                  .
                </p>
              </EmptyState>
            ) : (
              <div className="space-y-6">
                {dayGroups.map((group) => {
                  const headerDate = new Date(group.dayStartMs);
                  const headerLabel = headerDate.toLocaleDateString("en-US", {
                    weekday: "short",
                    month: "short",
                    day: "numeric",
                  });
                  const totalLabel = formatDuration(group.totalMs, {
                    short: true,
                    maxUnits: 2,
                  });
                  return (
                    <div key={group.dayKey}>
                      <div className="flex items-baseline justify-between py-2 brut-divider">
                        <span className="text-base font-black text-ink dark:text-cream uppercase tracking-wider">
                          {headerLabel}
                        </span>
                        <span className="text-sm font-black text-crust-500 dark:text-lemon-500 font-mono">
                          {totalLabel}
                        </span>
                      </div>
                      <div className="space-y-1.5 mt-3">
                        {group.entries.map((entry) => {
                const { id, description, startTime, endTime, clientId } =
                  entry;
                const isRunning = !endTime;
                const isCurrentEntry = id === currentEntry;
                // Smoothed rows are derived and read-only: never enter the edit
                // form, never show row actions.
                const isEditing = !showSmoothed && id === editingEntry;
                const mergedCount = isSmoothed(entry)
                  ? entry.sourceIds.length
                  : 1;

                const startTimeStr = new Date(startTime).toLocaleTimeString(
                  "en-US",
                  {
                    hour: "numeric",
                    minute: "2-digit",
                    hour12: true,
                  },
                );

                const endTimeStr = endTime
                  ? new Date(endTime).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                      hour12: true,
                    })
                  : "";

                const timeDisplay = isRunning
                  ? `${startTimeStr} - running`
                  : `${startTimeStr} - ${endTimeStr}`;

                const durationMilliseconds = isRunning
                  ? Date.now() - startTime
                  : endTime! - startTime;
                const formattedDuration = formatDuration(durationMilliseconds, {
                  round: true,
                });

                return isEditing ? (
                  <Card key={id} sub className="p-4">
                    <div className="space-y-3">
                      <Input
                        type="text"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                        placeholder="Description"
                      />

                      <div>
                        <label className="block text-xs font-bold mb-1 text-ink dark:text-cream">
                          Client
                        </label>
                        <Select
                          value={editClientId}
                          onChange={(e) => setEditClientId(e.target.value)}
                        >
                          {editClientOptions.map((client) => (
                            <option key={client.id} value={client.id}>
                              {client.name}
                            </option>
                          ))}
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-bold mb-1 text-ink dark:text-cream">
                            Start Time
                          </label>
                          <DatePicker
                            selected={editStartTime}
                            onChange={(date: Date | null) => setEditStartTime(date)}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            placeholderText="Select start date and time"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-bold mb-1 text-ink dark:text-cream">
                            End Time
                          </label>
                          <DatePicker
                            selected={editEndTime}
                            onChange={(date: Date | null) => setEditEndTime(date)}
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={15}
                            dateFormat="MMMM d, yyyy h:mm aa"
                            placeholderText="Select end date and time"
                            isClearable
                          />
                        </div>
                      </div>
                      <div className="flex justify-end gap-2">
                        <Button onClick={cancelEdit}>
                          Cancel
                        </Button>
                        <Button
                          onClick={saveEdit}
                          variant="primary"
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  </Card>
                ) : (
                  <div
                    key={id}
                    className="grid grid-cols-12 gap-4 py-3 px-2 brut-divider-thin hover:bg-lemon-100 dark:hover:bg-charcoal animate-entry-in rounded"
                  >
                    <div className="col-span-6 flex items-center">
                      <svg
                        className="w-4 h-4 mr-1.5 text-ink-soft dark:text-cream/70 flex-shrink-0"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <circle cx="12" cy="12" r="10" strokeWidth="2.5" />
                        <path
                          d="M12 6v6l4 2"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                        />
                      </svg>
                      <span className="font-black text-ink dark:text-cream">
                        {formattedDuration}
                      </span>
                      <span className="text-xs text-ink-soft dark:text-cream/60 ml-1.5 font-medium">
                        ( {timeDisplay} )
                      </span>
                    </div>
                    <div className="col-span-5 flex items-center min-w-0">
                      <div className="mr-2 flex-shrink-0">
                        {getClientLabel(isRunning ? "running" : clientId)}
                      </div>
                      <span className="font-bold text-ink dark:text-cream truncate">
                        {description}
                      </span>
                      {mergedCount > 1 && (
                        <Badge
                          className="ml-2 flex-shrink-0"
                          title={`${mergedCount} entries merged`}
                        >
                          {mergedCount}
                        </Badge>
                      )}
                    </div>
                    <div className="col-span-1 flex justify-end items-center gap-1">
                      {!showSmoothed && (
                        <>
                          {isRunning && !isCurrentEntry && (
                        <Button
                          onClick={() => resumeTimer(id)}
                          icon
                          title="Resume"
                          aria-label="Resume"
                        >
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z"
                              fill="currentColor"
                            />
                            <path
                              d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                              strokeWidth="2.5"
                            />
                          </svg>
                        </Button>
                      )}

                      {!isRunning && (
                        <Button
                          onClick={() => startEditingEntry(entry)}
                          icon
                          title="Edit"
                          aria-label="Edit entry"
                        >
                          <svg
                            className="w-4 h-4"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            <path
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                              strokeWidth="2.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </Button>
                      )}

                      <Button
                        onClick={() => handleDeleteEntry(id)}
                        icon
                        variant="danger"
                        title="Delete"
                        aria-label="Delete entry"
                      >
                        <svg
                          className="w-4 h-4"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            </div>
          </>
        )}
      </div>

      {/* Settings modal */}
      <SettingsModal
        isOpen={modal === "settings"}
        onClose={() => setModal("none")}
      />

      {/* Modal for task description */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4">
          <Card className="p-6 w-full max-w-md">
            <h2 className="text-xl font-black mb-4 dark:text-cream">
              What did you work on?
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-bold text-ink dark:text-cream mb-1">
                  Description
                </label>
                <Input
                  autoFocus
                  type="text"
                  value={modalDescription}
                  onChange={(e) => setModalDescription(e.target.value)}
                  placeholder="What did you work on?"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-ink dark:text-cream mb-1">
                  Client
                </label>
                <fieldset className="space-y-1 mt-1" role="radiogroup">
                  <legend className="sr-only">Select a client</legend>
                  {pickerClients.map((client, index) => {
                    const checked = modalClientId === client.id;
                    const shortcut = index < 9 ? index + 1 : null;
                    return (
                      <label
                        key={client.id}
                        htmlFor={`client-${client.id}`}
                        className={`flex items-center gap-2 text-sm font-bold cursor-pointer w-full py-2 px-3 border-2 rounded transition-all ${
                          checked
                            ? "border-ink dark:border-cream bg-lemon-500 text-ink"
                            : "border-ink/30 dark:border-cream/30 text-ink dark:text-cream hover:border-ink dark:hover:border-cream"
                        }`}
                      >
                        <input
                          id={`client-${client.id}`}
                          type="radio"
                          name="modalClient"
                          value={client.id}
                          checked={checked}
                          onChange={() => setModalClientId(client.id)}
                          className="sr-only"
                          aria-checked={checked}
                        />
                        <span
                          className="h-3 w-3 rounded-full inline-block border-2 border-ink"
                          style={{ backgroundColor: client.color }}
                          aria-hidden="true"
                        />
                        <span className="flex-1">{client.name}</span>
                        {shortcut !== null && (
                          <Kbd
                            aria-label={`Press ${shortcut} to save with this client`}
                          >
                            {shortcut}
                          </Kbd>
                        )}
                      </label>
                    );
                  })}
                </fieldset>
              </div>

              <div className="flex justify-end gap-2 pt-2 flex-wrap">
                <Button
                  onClick={discardFromModal}
                  variant="danger"
                >
                  Discard <Kbd>⌘⌫</Kbd>
                </Button>
                <Button onClick={resumeFromModal}>
                  Resume <Kbd>Esc</Kbd>
                </Button>
                <Button
                  onClick={() => saveEntryFromModal()}
                  variant="primary"
                  disabled={modalDescription.trim() === ""}
                >
                  Save <Kbd>Enter</Kbd>
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default TimeTracker;
